"use client";

import React from "react";
import { FileText, Image as ImageIcon, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";

interface UploadProgressItemProps {
  fileName: string;
  fileSize: number;
  fileType: string;
  progress: number;
  status: "uploading" | "completed" | "error";
  error?: string;
  onCancel?: () => void;
}

export const UploadProgressItem: React.FC<UploadProgressItemProps> = ({
  fileName,
  fileSize,
  progress,
  status,
  error,
  onCancel,
}) => {
  const { isDark } = useTheme();
  const formatFileSize = (size: number) => {
    const units = ["B", "KB", "MB", "GB"];
    let unitIndex = 0;
    let fileSize = size;

    while (fileSize >= 1024 && unitIndex < units.length - 1) {
      fileSize /= 1024;
      unitIndex++;
    }

    return `${fileSize.toFixed(1)} ${units[unitIndex]}`;
  };

  const getFileIcon = () => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"];

    if (extension && imageExtensions.includes(extension)) {
      return <ImageIcon className="w-8 h-8 text-green-500" />;
    }
    return (
      <FileText
        className={`w-8 h-8 ${isDark ? "text-gray-300" : "text-gray-500"}`}
      />
    );
  };

  const getStatusColor = () => {
    switch (status) {
      case "uploading":
        return "bg-blue-500";
      case "completed":
        return "bg-green-500";
      case "error":
        return "bg-red-500";
      default:
        return "bg-gray-300";
    }
  };

  const getStatusText = () => {
    switch (status) {
      case "uploading":
        return `${progress}%`;
      case "completed":
        return "Complete";
      case "error":
        return "Failed";
      default:
        return "";
    }
  };

  return (
    <div className="flex flex-col">
      {/* Upload progress card */}
      <div
        className={`relative rounded-lg border overflow-hidden opacity-75 aspect-square ${
          isDark
            ? "border-auth-background bg-auth-background"
            : "border-gray-200 bg-gray-50"
        }`}
      >
        {/* Progress overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {getFileIcon()}

          {/* Progress bar */}
          <div className="w-3/4 bg-gray-200 rounded-full h-2 mt-4">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${getStatusColor()}`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            ></div>
          </div>

          {/* Status text */}
          <div
            className={`mt-2 text-xs font-medium ${isDark ? "text-gray-200" : "text-gray-700"}`}
          >
            {getStatusText()}
          </div>
        </div>

        {/* Cancel button (only show during upload) */}
        {status === "uploading" && onCancel && (
          <Button
            variant="ghost"
            size="sm"
            className={`absolute top-2 right-2 h-6 w-6 p-0 hover:text-red-600 z-10 rounded-full ${
              isDark
                ? "text-gray-300 bg-auth-card/60 hover:bg-auth-card/80"
                : "text-gray-600 bg-white/60 hover:bg-white/80"
            }`}
            onClick={onCancel}
          >
            <X className="w-4 h-4" />
          </Button>
        )}

        {/* Error overlay */}
        {status === "error" && (
          <div className="absolute inset-0 bg-red-50 border-red-200 border-2 rounded-lg flex items-center justify-center">
            <div className="text-center p-2">
              <div className="text-red-600 text-xs font-medium mb-1">
                Upload Failed
              </div>
              {error && (
                <div
                  className="text-red-500 text-xs truncate max-w-full"
                  title={error}
                >
                  {error}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* File information outside the card */}
      <div className="mt-2 text-center px-1">
        <div
          className={`font-medium text-xs truncate mb-1 overflow-clip w-full ${isDark ? "text-gray-100" : "text-gray-900"}`}
          title={fileName}
        >
          {fileName}
        </div>
        <div
          className={`text-xs ${isDark ? "text-gray-300" : "text-gray-500"}`}
        >
          {formatFileSize(fileSize)}
        </div>
      </div>
    </div>
  );
};
